<template>
  <rmr-modal
    :value="value"
    variant="form"
    :icon="icons.mdiPaletteOutline"
    eyebrow="Produits similaires"
    title="Ajouter un coloris"
    :subtitle="group ? `À « ${group.title} » uniquement` : ''"
    max-width="900"
    @input="$emit('input', $event)"
  >
    <template #body>
      <v-card-text v-if="group" class="ctc-variant-picker">
        <div class="ctc-variant-picker__target">
          <span class="ctc-variant-picker__media">
            <img
              v-if="group.products[0].imageUrl"
              :src="group.products[0].imageUrl"
              :alt="group.products[0].title"
            />
            <v-icon v-else :color="subtleColor">
              {{ icons.mdiImageOffOutline }}
            </v-icon>
          </span>
          <span>
            <small>Le coloris sera regroupé avec ce produit</small>
            <strong>{{ group.products[0].title }}</strong>
          </span>
        </div>
        <p class="ctc-variant-picker__help">
          Choisissez seulement une autre couleur ou déclinaison du même produit.
          Un produit différent doit être ajouté depuis la sélection principale.
        </p>
        <form class="ctc-variant-picker__search" @submit.prevent="search">
          <rmr-text-field
            v-model="searchTerm"
            label="Rechercher les coloris de ce produit"
            aria-label="Rechercher les coloris de ce produit"
          />
          <rmr-action-button
            label="Rechercher"
            :icon="icons.mdiMagnify"
            size="sm"
            type="submit"
            :loading="loading"
            :disabled="searchTerm.trim().length < 2"
          />
        </form>

        <div v-if="loading" class="ctc-variant-picker__status" role="status">
          <v-progress-circular
            indeterminate
            size="22"
            width="2"
            color="primary"
          />
          Recherche des coloris…
        </div>
        <div
          v-else-if="error"
          class="ctc-variant-picker__status ctc-variant-picker__status--error"
          role="alert"
        >
          <span>{{ error }}</span>
          <rmr-action-button
            label="Réessayer"
            variant="ghost"
            size="sm"
            @click="search"
          />
        </div>
        <div
          v-else-if="!availableCandidates.length"
          class="ctc-variant-picker__status"
        >
          Aucun autre coloris disponible pour cette recherche.
        </div>
        <ctc-similar-product-cards
          v-else
          :products="availableCandidates"
          :can-select="canAdd"
          action-label="Ajouter ce coloris"
          @select="$emit('add', $event)"
        />
      </v-card-text>
    </template>
    <template #footer>
      <rmr-action-button
        label="Terminer"
        variant="ghost"
        @click="$emit('input', false)"
      />
    </template>
  </rmr-modal>
</template>

<script>
import { computed, ref, watch } from "@vue/composition-api";
import { mdiImageOffOutline, mdiMagnify, mdiPaletteOutline } from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";
import { RMR_TEXT_SUBTLE } from "@/styles/tokens";

import CtcSimilarProductCards from "../shared/CtcSimilarProductCards.vue";

/** Searches the colours of one selected model and adds them to its tab. */
export default {
  name: "CtcVariantPickerModal",
  components: {
    CtcSimilarProductCards,
    RmrActionButton,
    RmrModal,
    RmrTextField,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    group: {
      type: Object,
      default: null,
    },
    candidates: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    canAdd: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const searchTerm = ref("");

    const search = () => {
      const query = searchTerm.value.trim();
      if (query.length >= 2) emit("search", query);
    };

    watch(
      () => props.value,
      (open) => {
        if (!open || !props.group) return;
        searchTerm.value = props.group.title;
        search();
      }
    );

    return {
      availableCandidates: computed(() =>
        props.candidates.filter(
          (product) => !props.selectedIds.includes(product.productLocalizedId)
        )
      ),
      icons: { mdiImageOffOutline, mdiMagnify, mdiPaletteOutline },
      search,
      searchTerm,
      subtleColor: RMR_TEXT_SUBTLE,
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-variant-picker {
  padding: 20px !important;
  background: $rmr-surface-2;
}

.ctc-variant-picker__target {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid $rmr-border-hover;
}

.ctc-variant-picker__media {
  display: flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-md;
  background: $rmr-surface-0;
}

.ctc-variant-picker__media img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ctc-variant-picker__target strong,
.ctc-variant-picker__target small {
  display: block;
}

.ctc-variant-picker__target strong {
  margin-top: 3px;
  color: $rmr-text-primary;
  font-size: $rmr-fs-base;
}

.ctc-variant-picker__target small {
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
}

.ctc-variant-picker__help {
  margin: 0 0 16px;
  color: $rmr-text-secondary;
  font-size: $rmr-fs-base;
}

.ctc-variant-picker__search {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
  margin: 4px 0 14px;
}

.ctc-variant-picker__status {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px 0;
  color: $rmr-text-secondary;
  font-size: $rmr-fs-base;
}

.ctc-variant-picker__status--error {
  color: $rmr-red;
}

@media (max-width: 760px) {
  .ctc-variant-picker__search {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
